import React, { useState } from 'react';
import BodyMapSVG from './BodyMapSVG';

/**
 * MobilitySilhouette — hybrid mobility body map.
 *
 *   Layer 1: anatomical body + muscle tint (soft, low-opacity) → shows
 *            WHICH tissue typically restricts each joint's ROM. Acts as
 *            background CONTEXT.
 *
 *   Layer 2: joint dots overlaid at landmark positions → shows the actual
 *            mobility score we measured per joint, tinted by score band.
 *            Each dot is hover-able and reveals the L/R values for that
 *            joint plus asymmetry delta if flagged.
 *
 * We MEASURE joints (shoulder/trunk/hip/knee/ankle/neck flexion or
 * rotation). The muscle tint is a clinically-meaningful proxy for the
 * tissue limiting that joint's ROM. Layering both lets the user see
 * BOTH at the same time without conceptual confusion.
 */

// Color band shared across MobilityCard, MobilitySilhouette, /mobility.
const colorForScore = (s) => {
  if (s == null) return '#3F3F46';
  if (s >= 85) return '#22C55E';
  if (s >= 70) return '#4ADE80';
  if (s >= 55) return '#FDE047';
  if (s >= 40) return '#FB923C';
  if (s >= 25) return '#F87171';
  return '#EF4444';
};

const avg = (a, b) => {
  const vals = [a, b].filter(v => v != null);
  if (!vals.length) return null;
  return Math.round(vals.reduce((x, y) => x + y, 0) / vals.length);
};

// Muscle keys → which joint region(s) restrict that muscle's tissue
// drives the soft background tint.
const MUSCLE_TO_REGIONS = {
  shoulders:  { regions: ['shoulder_L', 'shoulder_R'], label: 'Shoulder flexion ROM' },
  chest:      { regions: ['shoulder_L', 'shoulder_R'], label: 'Shoulder flexion ROM' },
  upper_back: { regions: ['spine_L', 'spine_R'],       label: 'Side bend ROM' },
  core:       { regions: ['core_lumbar'],              label: 'Lumbar hinge ROM' },
  lower_back: { regions: ['core_lumbar'],              label: 'Lumbar hinge ROM' },
  glutes:     { regions: ['hip_L', 'hip_R'],           label: 'Hip flexion ROM' },
  hamstrings: { regions: ['hip_L', 'hip_R'],           label: 'Hip flexion ROM' },
  calves:     { regions: ['ankle_L', 'ankle_R'],       label: 'Ankle dorsiflexion' },
};

const buildMuscleData = (regionScores = {}) => {
  const out = {};
  for (const [muscleKey, def] of Object.entries(MUSCLE_TO_REGIONS)) {
    const score = def.regions.length > 1
      ? avg(regionScores[def.regions[0]], regionScores[def.regions[1]])
      : regionScores[def.regions[0]] ?? null;
    if (score != null) {
      // Clamp at 99 so BodyMapSVG (which dims at exactly 100 in recovery
      // mode) still activates the tint for perfect mobility scores.
      out[muscleKey] = { color: colorForScore(score), recovery_pct: Math.min(score, 99) };
    }
  }
  return out;
};

// ─── Joint-dot positions (SVG viewBox 200×440 for each body view) ────
//
// Tuned by eye to land on actual landmark positions of the existing
// BodyMapSVG illustration. Front/back share most positions but the
// trunk-rotation dots only really make sense from the back view.

const FRONT_JOINTS = [
  { region: 'neck',       cx: 100, cy: 58,  bilateral: true,  label: 'NECK TILT' },
  { region: 'shoulder_L', cx: 60,  cy: 74,  side: 'L',        label: 'L SHOULDER' },
  { region: 'shoulder_R', cx: 140, cy: 74,  side: 'R',        label: 'R SHOULDER' },
  { region: 'spine_L',    cx: 80,  cy: 150, side: 'L',        label: 'L SIDE BEND' },
  { region: 'spine_R',    cx: 120, cy: 150, side: 'R',        label: 'R SIDE BEND' },
  { region: 'core_lumbar',cx: 100, cy: 180, bilateral: false, label: 'CORE / LUMBAR' },
  { region: 'hip_L',      cx: 84,  cy: 217, side: 'L',        label: 'L HIP' },
  { region: 'hip_R',      cx: 116, cy: 217, side: 'R',        label: 'R HIP' },
  { region: 'ankle_L',    cx: 78,  cy: 388, side: 'L',        label: 'L ANKLE' },
  { region: 'ankle_R',    cx: 122, cy: 388, side: 'R',        label: 'R ANKLE' },
];

const BACK_JOINTS = [
  { region: 'spine_L',    cx: 88,  cy: 150, side: 'L',        label: 'L SIDE BEND' },
  { region: 'spine_R',    cx: 112, cy: 150, side: 'R',        label: 'R SIDE BEND' },
  // The rest mirror the front dots so users see the joint scores from both views.
  { region: 'shoulder_L', cx: 60,  cy: 74,  side: 'L',        label: 'L SHOULDER' },
  { region: 'shoulder_R', cx: 140, cy: 74,  side: 'R',        label: 'R SHOULDER' },
  { region: 'hip_L',      cx: 84,  cy: 217, side: 'L',        label: 'L HIP (glute)' },
  { region: 'hip_R',      cx: 116, cy: 217, side: 'R',        label: 'R HIP (glute)' },
  { region: 'ankle_L',    cx: 78,  cy: 388, side: 'L',        label: 'L ANKLE' },
  { region: 'ankle_R',    cx: 122, cy: 388, side: 'R',        label: 'R ANKLE' },
];

const JointDot = ({ joint, regionScores, asymByRegion, onHover, isHovered }) => {
  let score;
  if (joint.bilateral) {
    // single dot represents both sides combined (neck)
    score = avg(regionScores[`${joint.region}_L`], regionScores[`${joint.region}_R`])
      ?? regionScores[joint.region]
      ?? null;
  } else {
    score = regionScores[joint.region];
  }
  const color = colorForScore(score);
  const isAsym = Boolean(asymByRegion[joint.region.replace('_L', '').replace('_R', '')]);
  const r = isHovered ? 8 : 6;

  return (
    <g
      onMouseEnter={() => onHover(joint)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => { e.stopPropagation(); onHover(isHovered ? null : joint); }}
      style={{ cursor: 'pointer' }}
    >
      {/* Enlarged transparent hit area so taps land easily on mobile */}
      <circle cx={joint.cx} cy={joint.cy} r={14} fill="transparent" />
      {/* Outer pulse ring for asymmetric joints */}
      {isAsym && score != null && (
        <circle cx={joint.cx} cy={joint.cy} r={r + 4} fill="none" stroke="#F59E0B" strokeWidth="0.5" strokeOpacity="0.6">
          <animate attributeName="r" values={`${r + 3};${r + 6};${r + 3}`} dur="2s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" values="0.6;0.1;0.6" dur="2s" repeatCount="indefinite" />
        </circle>
      )}
      {/* Score-tinted ring + fill */}
      <circle
        cx={joint.cx} cy={joint.cy} r={r}
        fill="#0A0A0A"
        stroke={color}
        strokeWidth={isHovered ? 2.2 : 1.6}
      />
      {/* Inner solid dot */}
      <circle cx={joint.cx} cy={joint.cy} r={r - 2.5} fill={color} fillOpacity={0.85} />
    </g>
  );
};

const buildOverlay = (joints, regionScores, asymByRegion, hovered, setHovered) =>
  joints.map(j => (
    <JointDot
      key={`${j.region}-${j.cx}-${j.cy}`}
      joint={j}
      regionScores={regionScores}
      asymByRegion={asymByRegion}
      onHover={setHovered}
      isHovered={hovered?.region === j.region && hovered?.cx === j.cx}
    />
  ));

const SIZE_TO_BODY_SIZE = { sm: 'sm', md: 'md', lg: 'lg' };

const MobilitySilhouette = ({
  regionScores = {},
  asymmetryFlags = [],
  size = 'md',
  showBack = true,
  interactive = true,
}) => {
  const [hoveredJoint, setHoveredJoint] = useState(null);
  const muscleData = buildMuscleData(regionScores);

  const asymByRegion = {};
  asymmetryFlags.forEach(f => { asymByRegion[f.region] = f; });

  const frontOverlay = interactive
    ? buildOverlay(FRONT_JOINTS, regionScores, asymByRegion, hoveredJoint, setHoveredJoint)
    : buildOverlay(FRONT_JOINTS, regionScores, asymByRegion, null, () => {});
  const backOverlay = interactive
    ? buildOverlay(BACK_JOINTS, regionScores, asymByRegion, hoveredJoint, setHoveredJoint)
    : buildOverlay(BACK_JOINTS, regionScores, asymByRegion, null, () => {});

  return (
    <div className="relative w-full h-full">
      <BodyMapSVG
        muscleData={muscleData}
        highlightMode="recovery"
        size={SIZE_TO_BODY_SIZE[size] || 'md'}
        showLabels={false}
        interactive={false}             // we use our own joint-dot hover
        showBack={showBack}
        frontOverlay={frontOverlay}
        backOverlay={backOverlay}
      />

      {/* Joint hover tooltip — absolutely-positioned over the body */}
      {interactive && hoveredJoint && (
        <div
          className="absolute top-2 right-2 bg-zinc-900/95 border border-zinc-700 rounded-sm px-3 py-2 pointer-events-none z-10 backdrop-blur-sm min-w-[180px]"
          data-testid="mobility-joint-tooltip"
        >
          {(() => {
            const region = hoveredJoint.region;
            const baseKey = region.replace('_L', '').replace('_R', '');
            const asym = asymByRegion[baseKey];

            if (hoveredJoint.bilateral) {
              const lScore = regionScores[`${baseKey}_L`];
              const rScore = regionScores[`${baseKey}_R`];
              return (
                <>
                  <p className="font-secondary font-bold text-white uppercase text-xs tracking-wider">{hoveredJoint.label}</p>
                  <div className="flex gap-3 mt-1">
                    <div>
                      <p className="text-[10px] text-zinc-500">LEFT</p>
                      <p className="font-secondary font-bold text-lg leading-none" style={{ color: colorForScore(lScore) }}>{lScore ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-500">RIGHT</p>
                      <p className="font-secondary font-bold text-lg leading-none" style={{ color: colorForScore(rScore) }}>{rScore ?? '—'}</p>
                    </div>
                  </div>
                </>
              );
            }
            // Sided dot — show this side's value prominently, plus the opposite
            const isL = hoveredJoint.side === 'L';
            const thisKey = isL ? `${baseKey}_L` : `${baseKey}_R`;
            const otherKey = isL ? `${baseKey}_R` : `${baseKey}_L`;
            const thisScore = regionScores[thisKey];
            const otherScore = regionScores[otherKey];
            return (
              <>
                <p className="font-secondary font-bold text-white uppercase text-xs tracking-wider">{hoveredJoint.label}</p>
                <p className="font-secondary font-bold text-2xl leading-none mt-1" style={{ color: colorForScore(thisScore) }}>
                  {thisScore ?? '—'}<span className="text-zinc-500 text-sm ml-1">/100</span>
                </p>
                {otherScore != null && (
                  <p className="text-[10px] text-zinc-500 mt-1">
                    opposite side: <span className="text-zinc-300" style={{ color: colorForScore(otherScore) }}>{otherScore}</span>
                  </p>
                )}
                {asym && (
                  <p className="text-[10px] text-amber-300 mt-1 pt-1 border-t border-amber-500/20">
                    {asym.delta} pt gap · weaker {asym.weaker_side === 'L' ? 'left' : 'right'}
                  </p>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Discovery hint — disappears once the user interacts */}
      {interactive && !hoveredJoint && (
        <div className="absolute bottom-1 inset-x-0 flex justify-center pointer-events-none z-10" data-testid="mobility-silhouette-hint">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-950/75 border border-zinc-800 rounded-full px-2.5 py-1 backdrop-blur-sm">
            Tap or hover a point to see L/R
          </span>
        </div>
      )}
    </div>
  );
};

export default MobilitySilhouette;
export { colorForScore };
